import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Breadcrumbs,
  Link,
  Typography,
  IconButton,
  Container,
  Stack,
  Button,
  TextField,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  PhotoLibrary as PhotoLibraryIcon,
  AddAPhoto as AddAPhotoIcon,
  UploadFile as UploadFileIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { ThemeContext } from '../../contexts/ThemeContext';
import UserProfileMenu from '../../components/UserProfileMenu';
import AuthorChip from '../../components/AuthorChip';
import usePermissions from '../../hooks/usePermissions';
import { SitePhotoService } from '../../services/SitePhotoService';
import {
  SitePhoto,
  PHOTO_CATEGORIES,
  PHOTO_CATEGORY_LABEL,
  isVideoPhoto,
} from '../../models/SitePhoto';

interface Snack {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

interface UploadForm {
  category: string;
  note: string;
  uploadedBy: string;
}

const EMPTY_UPLOAD: UploadForm = {
  category: 'rack',
  note: '',
  uploadedBy: '',
};

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' });
};

const formatSize = (bytes?: number) => {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const SitePhotosPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode, toggleTheme } = useContext(ThemeContext);
  const { canEdit, canAdmin } = usePermissions();
  const { projectId } = useParams<{ projectId: string }>();
  const projectIdNum = projectId ? Number(projectId) : NaN;

  const stored = localStorage.getItem('selectedProject');
  const projectName = stored ? (JSON.parse(stored).name as string | undefined) : undefined;

  const [photos, setPhotos] = useState<SitePhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadForm, setUploadForm] = useState<UploadForm>(EMPTY_UPLOAD);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState<SitePhoto | null>(null);
  const [editing, setEditing] = useState<SitePhoto | null>(null);
  const [editCategory, setEditCategory] = useState('');
  const [editNote, setEditNote] = useState('');
  const [snack, setSnack] = useState<Snack>({ open: false, message: '', severity: 'success' });

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    if (!Number.isFinite(projectIdNum)) return;
    setLoading(true);
    setError(null);
    try {
      const list = await SitePhotoService.list(projectIdNum, categoryFilter || undefined);
      setPhotos(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : '목록 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [projectIdNum, categoryFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const grouped = useMemo(() => {
    const map = new Map<string, SitePhoto[]>();
    photos.forEach((p) => {
      const arr = map.get(p.category) ?? [];
      arr.push(p);
      map.set(p.category, arr);
    });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [photos]);

  const openUploadDialog = (files: FileList | null, defaultCategory?: string) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (arr.length === 0) {
      setSnack({ open: true, message: '이미지 파일만 선택할 수 있습니다', severity: 'error' });
      return;
    }
    setSelectedFiles(arr);
    setUploadForm({
      ...EMPTY_UPLOAD,
      category: defaultCategory || categoryFilter || 'rack',
    });
    setUploadError(null);
    setUploadDialogOpen(true);
  };

  const handleCameraClick = () => cameraInputRef.current?.click();
  const handleFileClick = () => fileInputRef.current?.click();

  const handleUploadConfirm = async () => {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    setUploadError(null);
    try {
      // 순차 업로드 (서버 부담 ↓, 진행 상황 추적 명확)
      for (const file of selectedFiles) {
        await SitePhotoService.upload(projectIdNum, {
          file,
          category: uploadForm.category || 'other',
          note: uploadForm.note || undefined,
          uploadedBy: uploadForm.uploadedBy || undefined,
          takenAt: new Date(file.lastModified || Date.now()).toISOString(),
        });
      }
      setSnack({
        open: true,
        message: `${selectedFiles.length}장 업로드 완료`,
        severity: 'success',
      });
      setUploadDialogOpen(false);
      setSelectedFiles([]);
      await refresh();
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : '업로드 실패');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (p: SitePhoto) => {
    if (!window.confirm(`'${p.original_name}' 사진을 삭제하시겠습니까?`)) return;
    try {
      await SitePhotoService.delete(p.id);
      setSnack({ open: true, message: '삭제되었습니다', severity: 'success' });
      if (lightbox?.id === p.id) setLightbox(null);
      await refresh();
    } catch (e) {
      setSnack({ open: true, message: e instanceof Error ? e.message : '삭제 실패', severity: 'error' });
    }
  };

  const openEdit = (p: SitePhoto) => {
    setEditing(p);
    setEditCategory(p.category);
    setEditNote(p.note ?? '');
  };

  const handleEditSave = async () => {
    if (!editing) return;
    try {
      await SitePhotoService.update(editing.id, {
        category: editCategory,
        note: editNote,
      });
      setSnack({ open: true, message: '수정되었습니다', severity: 'success' });
      setEditing(null);
      await refresh();
    } catch (e) {
      setSnack({ open: true, message: e instanceof Error ? e.message : '수정 실패', severity: 'error' });
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 12 }}>
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 4,
          py: 1.5,
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate(`/project/${projectId}`)} size="small" aria-label="뒤로가기">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate('/projects')}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 500 }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              현장 목록
            </Link>
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate(`/project/${projectId}`)}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 500 }}
            >
              <BusinessIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              {projectName || `프로젝트 ${projectId}`}
            </Link>
            <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>
              <PhotoLibraryIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              동영상 및 이미지 보관함
            </Typography>
          </Breadcrumbs>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton
            onClick={toggleTheme}
            size="small"
            title={mode === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) },
            }}
          >
            {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>
          <UserProfileMenu />
        </Box>
      </Paper>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              동영상 및 이미지 보관함 ({photos.length}개)
            </Typography>
            <Box sx={{ flex: 1 }} />
            <TextField
              select
              size="small"
              label="카테고리"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">전체</MenuItem>
              {PHOTO_CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {PHOTO_CATEGORY_LABEL[c] ?? c}
                </MenuItem>
              ))}
            </TextField>
            <Tooltip title="새로고침">
              <span>
                <IconButton onClick={refresh} disabled={loading} aria-label="새로고침">
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            {canEdit && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={handleFileClick}
                >
                  파일 선택
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddAPhotoIcon />}
                  onClick={handleCameraClick}
                >
                  사진 촬영
                </Button>
              </>
            )}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={(e) => {
                openUploadDialog(e.target.files);
                e.target.value = '';
              }}
              data-testid="camera-input"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => {
                openUploadDialog(e.target.files);
                e.target.value = '';
              }}
              data-testid="file-input"
            />
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : photos.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
              <PhotoLibraryIcon sx={{ fontSize: 48, opacity: 0.4, mb: 1 }} />
              <Typography variant="body2">아직 등록된 사진이 없습니다.</Typography>
              <Typography variant="caption">"사진 촬영" 또는 "파일 선택"으로 시작하세요.</Typography>
            </Box>
          ) : (
            grouped.map(([cat, list]) => (
              <Box key={cat} sx={{ mb: 4 }} data-testid={`category-section-${cat}`}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <Chip label={PHOTO_CATEGORY_LABEL[cat] ?? cat} color="primary" variant="outlined" />
                  <Typography variant="caption" color="text.secondary">
                    {list.length}장
                  </Typography>
                </Stack>
                <ImageList cols={3} gap={8} sx={{ m: 0 }}>
                  {list.map((p) => (
                    <ImageListItem
                      key={p.id}
                      sx={{ cursor: 'pointer', borderRadius: 1, overflow: 'hidden' }}
                      onClick={() => setLightbox(p)}
                      data-testid={`photo-tile-${p.id}`}
                    >
                      {isVideoPhoto(p) ? (
                        // 동영상은 <img> 로 그리면 엑박 — 첫 프레임 썸네일로 표시.
                        <video
                          src={SitePhotoService.fileUrl(p.id)}
                          muted
                          preload="metadata"
                          style={{ width: '100%', height: 180, objectFit: 'cover', display: 'block' }}
                        />
                      ) : (
                        <img
                          src={SitePhotoService.fileUrl(p.id)}
                          alt={p.original_name}
                          loading="lazy"
                          style={{ width: '100%', height: 180, objectFit: 'cover' }}
                        />
                      )}
                      <ImageListItemBar
                        title={p.original_name}
                        subtitle={
                          <Box>
                            <span>{`${formatDate(p.taken_at ?? p.created_at)} · ${formatSize(p.size_bytes)}`}</span>
                            {(p.created_by_name || p.created_by_id) && (
                              <Box sx={{ mt: 0.5 }} onClick={(e) => e.stopPropagation()}>
                                <AuthorChip name={p.created_by_name} userId={p.created_by_id} label="" dense />
                              </Box>
                            )}
                          </Box>
                        }
                        actionIcon={
                          canEdit ? (
                            <Box sx={{ pr: 1 }}>
                              <Tooltip title="메타 수정">
                                <IconButton
                                  size="small"
                                  sx={{ color: 'rgba(255,255,255,0.85)' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEdit(p);
                                  }}
                                  aria-label="수정"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {/* 사진 삭제는 admin 전용 — backend matrix 와 sync. */}
                              {canAdmin && (
                                <Tooltip title="삭제">
                                  <IconButton
                                    size="small"
                                    sx={{ color: 'rgba(255,200,200,0.85)' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(p);
                                    }}
                                    aria-label="삭제"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          ) : undefined
                        }
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              </Box>
            ))
          )}
        </Paper>
      </Container>

      {/* Upload dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => !uploading && setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>사진 업로드 ({selectedFiles.length}장)</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="카테고리 *"
              value={uploadForm.category}
              onChange={(e) => setUploadForm((f) => ({ ...f, category: e.target.value }))}
              fullWidth
            >
              {PHOTO_CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {PHOTO_CATEGORY_LABEL[c] ?? c}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="메모"
              value={uploadForm.note}
              onChange={(e) => setUploadForm((f) => ({ ...f, note: e.target.value }))}
              placeholder="예: 정문 좌측 랙 전경"
              multiline
              minRows={2}
              fullWidth
            />
            <TextField
              label="업로더 이름 (선택)"
              value={uploadForm.uploadedBy}
              onChange={(e) => setUploadForm((f) => ({ ...f, uploadedBy: e.target.value }))}
              fullWidth
            />
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                선택된 파일:
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {selectedFiles.map((f, i) => (
                  <Typography key={i} variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    • {f.name} · {formatSize(f.size)}
                  </Typography>
                ))}
              </Stack>
            </Box>
            {uploadError && <Alert severity="error">{uploadError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
            취소
          </Button>
          <Button variant="contained" onClick={handleUploadConfirm} disabled={uploading || selectedFiles.length === 0}>
            {uploading ? '업로드 중...' : '업로드'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lightbox */}
      <Dialog open={lightbox != null} onClose={() => setLightbox(null)} maxWidth="lg" fullWidth>
        {lightbox && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {lightbox.original_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {PHOTO_CATEGORY_LABEL[lightbox.category] ?? lightbox.category} ·{' '}
                  {formatDate(lightbox.taken_at ?? lightbox.created_at)} · {formatSize(lightbox.size_bytes)}
                </Typography>
              </Box>
              <IconButton onClick={() => setLightbox(null)} aria-label="닫기">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0, bgcolor: 'black', display: 'flex', justifyContent: 'center' }}>
              {isVideoPhoto(lightbox) ? (
                <video
                  src={SitePhotoService.fileUrl(lightbox.id)}
                  controls
                  autoPlay
                  style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block' }}
                />
              ) : (
                <img
                  src={SitePhotoService.fileUrl(lightbox.id)}
                  alt={lightbox.original_name}
                  style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                />
              )}
            </DialogContent>
            {lightbox.note && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2">{lightbox.note}</Typography>
              </Box>
            )}
          </>
        )}
      </Dialog>

      {/* Edit metadata dialog */}
      <Dialog open={editing != null} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        {editing && (
          <>
            <DialogTitle>사진 메타데이터 수정</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  select
                  label="카테고리"
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                  fullWidth
                >
                  {PHOTO_CATEGORIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {PHOTO_CATEGORY_LABEL[c] ?? c}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="메모"
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  multiline
                  minRows={3}
                  fullWidth
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditing(null)}>취소</Button>
              <Button variant="contained" onClick={handleEditSave}>
                저장
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SitePhotosPage;
